import React, { useState, useEffect } from 'react';
import { X, Save, Upload, User } from 'lucide-react';
import { api } from '@/utils/api';

interface AddCertModalProps {
  onClose: () => void;
  onSave: (certData: any) => Promise<void>;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  role: string;
}

const CERT_TYPES = [
  'Tower Climbing',
  'Fall Protection',
  'Rescue/Competent Climber',
  'First Aid',
  'CPR',
  'RF Awareness',
  'OSHA 10',
  'OSHA 30',
  'Forklift',
  'Aerial Lift',
  'Rigging',
  'CDL',
  'Other',
];

export function AddCertModal({ onClose, onSave }: AddCertModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    userId: '',
    certType: 'Tower Climbing',
    certName: '',
    certNumber: '',
    issueDate: '',
    expirationDate: '',
    documentUrl: '',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/jha/crew-members');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        userId: parseInt(formData.userId),
        issueDate: formData.issueDate || null,
        expirationDate: formData.expirationDate || null,
        certNumber: formData.certNumber || null,
        documentUrl: formData.documentUrl || null,
      };

      if (formData.expirationDate && formData.expirationDate < formData.issueDate) throw new Error('Expiration date cannot be before issue date');
      if (file) {
        if (file.size > 10 * 1024 * 1024) throw new Error('Document must be 10 MB or smaller');
        const multipart = new FormData();
        for (const [key, value] of Object.entries(submitData)) multipart.append(key, value == null ? '' : String(value));
        multipart.append('file', file);
        await onSave(multipart);
      } else await onSave(submitData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to add certification');
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Add Certification</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error:</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
          )}

          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee <span className="text-red-500">*</span>
            </label>
            <select
              name="userId"
              value={formData.userId}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select employee...</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} - {user.role}
                </option>
              ))}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Certification Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certification Type <span className="text-red-500">*</span>
              </label>
              <select
                name="certType"
                value={formData.certType}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {CERT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Certification Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certification Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="certName"
                value={formData.certName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., NATE Level 1"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Cert Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certification Number
              </label>
              <input
                type="text"
                name="certNumber"
                value={formData.certNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., TC-12345"
              />
            </div>

            {/* Issue Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issue Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiration Date
            </label>
            <input
              type="date"
              name="expirationDate"
              value={formData.expirationDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave blank if certification does not expire
            </p>
          </div>

          {/* Document URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document URL
            </label>
            <input
              type="url"
              name="documentUrl"
              value={formData.documentUrl}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com/cert.pdf"
            />
            <p className="mt-1 text-xs text-gray-500">
              URL to the certification document (optional)
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label htmlFor="certificate-file" className="block font-semibold text-blue-900 mb-2">Upload certificate document</label>
            <input id="certificate-file" type="file" accept="application/pdf,image/png,image/jpeg" disabled={saving}
              onChange={e => setFile(e.target.files?.[0] || null)} />
            <p className="text-sm text-blue-800 mt-2">PDF, PNG, or JPEG, up to 10 MB. An uploaded file takes priority over a document URL. Access follows the certificate's company and share permissions.</p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Add Certification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

